import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { ToastContainer } from 'react-toastify'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { QueryClientProvider } from '@tanstack/react-query'
import './i18n'
import App from './App'
import './index.css'
import 'react-toastify/dist/ReactToastify.css'
import { growthbook, loadGrowthbookFeatures } from './lib/growthbookClient'
import { queryClient } from './lib/queryClient'

loadGrowthbookFeatures()

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Capture 10% of transactions in production, 100% otherwise
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Capture 10% of sessions for replay in production, none otherwise
    replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Scrub any accidentally captured passwords or tokens from request bodies
      if (event.request?.data) {
        const data = event.request.data
        if (typeof data === 'object' && data !== null) {
          const scrubbed = { ...data }
          for (const key of ['password', 'token', 'refresh_token', 'access_token', 'id_token']) {
            if (key in scrubbed) scrubbed[key] = '[Filtered]'
          }
          event.request.data = scrubbed
        }
      }
      return event
    },
    enableLogs: true,
  })
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
          <p>Something went wrong. Please refresh the page.</p>
          {import.meta.env.MODE !== 'production' && (
            <pre style={{ marginTop: '1rem', fontSize: '0.75rem', textAlign: 'left' }}>
              {String(error)}
            </pre>
          )}
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <GrowthBookProvider growthbook={growthbook}>
          <App />
        </GrowthBookProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
    <ToastContainer position="top-center" />
  </React.StrictMode>
)
